/**
 * Language lists, in every shape they travel — jobs and candidate resumes
 * alike. Stored records hold [{ language, proficiency }] pairs; legacy rows
 * hold plain strings, pairs with a null level, or composites like
 * "English — Fluent". Everything downstream of the API derives from these
 * helpers rather than restating the shapes.
 */
import { z } from "zod";

// Pair caps, shared with the inline-create guards so entry time and submit
// time refuse with one voice. Proficiency deliberately follows the job
// payload's tighter limit (LanguageRequirement.proficiency ≤ 50), not the
// option endpoint's looser 100, so a level that could never ride on a job
// can never be committed either.
export const LANGUAGE_MAX_LENGTH = 100;
export const PROFICIENCY_MAX_LENGTH = 50;

// A composite carries its level after an em dash; the first dash splits,
// because levels themselves never contain one.
const splitComposite = (value) => {
  const text = String(value).trim();
  const sep = text.indexOf("—");
  if (sep <= 0) return { language: text, proficiency: "" };
  return { language: text.slice(0, sep).trim(), proficiency: text.slice(sep + 1).trim() };
};

// API → form rows. A missing proficiency becomes "" — allowed: a job (or
// candidate) may name a language without a level, and the backend stores it
// with a null proficiency.
export const toLanguageRows = (items) =>
  (Array.isArray(items) ? items : [])
    .map((item) =>
      typeof item === "string"
        ? splitComposite(item)
        : {
            language: String(item?.language ?? "").trim(),
            proficiency: item?.proficiency == null ? "" : String(item.proficiency).trim(),
          },
    )
    .filter((row) => row.language);

// API → plain strings, "English — Fluent". A requirement with no level
// renders as the bare language, matching how the JD prompt formats it.
// This is the one normalizer every consumer derives from: display chips,
// JD text, and the tag-input editors that crash on raw pairs (React #31),
// because candidate lists arrive in every shape at once — pairs, legacy
// strings, composites.
export const toLanguageStrings = (items) => toLanguageRows(items).map((row) => (row.proficiency ? `${row.language} — ${row.proficiency}` : row.language));

// A job language the job named a level for must carry one on the candidate's
// row: that level IS the answer being asked for. A job language named without a
// level ("any level") asks for none — see `levelledLanguageKeys`. Declared once
// here because two schemas enforce it — this file's `languagePairRowsSchema` and
// the review wizard's `languagePairRowsStrictSchema` — and one reworded message
// on one of them is a bug nobody writes.
export const REQUIRED_LANGUAGE_LEVEL_MESSAGE = "This job asks for a proficiency level in this language.";

// The two field-config keys a job's language rows drive, named once. They were
// retyped as string literals at every reader — the review schema, the Skills
// step's required marker — and a marker keyed off a different spelling from the
// rule that refuses the field is a lie the candidate only discovers at submit.
// Server side: `LANGUAGE_FIELD_KEYS` in app/config/field_schema.py.
export const LANGUAGE_MANDATORY_KEY = "languages.language";
export const PROFICIENCY_MANDATORY_KEY = "languages.proficiency";

// Which halves of the pair this job demands. The two are derived independently
// server-side (`mandatory_keys_for_job`), so they are answered independently
// here: a job naming "English, any level" turns on the language and not the
// level, and must not then ask the candidate for one.
export const languageFieldRequirements = (mandatoryFields = []) => ({
  language: (mandatoryFields ?? []).includes(LANGUAGE_MANDATORY_KEY),
  proficiency: (mandatoryFields ?? []).includes(PROFICIENCY_MANDATORY_KEY),
});

// The job languages whose level the candidate must answer: the ones the job
// itself named a level for, and only those.
//
// A job asking for "English, any level" has named a language and no level, so
// there is no level to answer — and the job's own Field Configuration agrees,
// because `languages.proficiency` only goes mandatory when some row carries a
// level (server: `mandatory_keys_for_job`). Demanding one here anyway refused a
// candidate who reported exactly what the job asked for, and contradicted the
// screen the recruiter had just filled in.
//
// A legacy bare string is a language with no level, so it levels nothing.
export const levelledLanguageKeys = (requiredLanguages) =>
  new Set(
    (requiredLanguages ?? [])
      .filter((entry) => typeof entry !== "string" && String(entry?.proficiency ?? "").trim())
      .map((entry) => String(entry.language ?? "").trim().toLowerCase())
      .filter(Boolean),
  );

// One language, one row — on a job and on a candidate's answer alike. Declared
// once because both pair schemas enforce it (this file's
// `languagePairRowsSchema`, the review wizard's `languagePairRowsStrictSchema`),
// and the server refuses the same duplicate with the same words
// (`_coerce_language_rows` in app/api/v2/candidate/schema/api.py).
export const DUPLICATE_LANGUAGE_MESSAGE = "This language has already been added.";

// For a `superRefine`: flags every row repeating an earlier row's language,
// compared trimmed and case-insensitively. Blank rows are not languages.
export const addDuplicateLanguageIssues = (rows, ctx) => {
  const seen = new Set();
  rows.forEach((row, index) => {
    const key = String(row?.language ?? "").trim().toLowerCase();
    if (!key) return;
    if (seen.has(key)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: [index, "language"], message: DUPLICATE_LANGUAGE_MESSAGE });
    }
    seen.add(key);
  });
};

// The paired-dropdown editor's rules, shared by the job form and the
// candidate profile flows. Optional as a whole unless `required`. Proficiency
// is OPTIONAL per row — a language may be named without a level (the backend
// stores it as null and matching keys off presence). A proficiency without
// its language is still refused, blank rows are tolerated here and stripped
// before submit, duplicates are blocked. Limits are the pair caps above.
//
// `requiredLanguages` is the candidate side's extra rule: a row naming a
// language THIS JOB ASKED FOR A LEVEL IN must carry one, because that level is
// the whole answer the candidate is being asked for. A language the job named
// without a level asks nothing further, and is left alone — see
// `levelledLanguageKeys`. The job form passes nothing at all and keeps
// proficiency optional everywhere, which is what a recruiter needs.
export const languagePairRowsSchema = ({ required = false, requiredLanguages = [] } = {}) => {
  const mustHaveLevel = levelledLanguageKeys(requiredLanguages);
  const base = z
    .array(
      z.object({
        language: z.string().trim().max(LANGUAGE_MAX_LENGTH, `Language must be ${LANGUAGE_MAX_LENGTH} characters or fewer`).optional().or(z.literal("")),
        proficiency: z.string().trim().max(PROFICIENCY_MAX_LENGTH, `Proficiency must be ${PROFICIENCY_MAX_LENGTH} characters or fewer`).optional().or(z.literal("")),
      }),
    )
    .superRefine((rows, ctx) => {
      addDuplicateLanguageIssues(rows, ctx);
      rows.forEach((row, index) => {
        // A stored record may carry neither key, so the optional chain alone
        // still leaves `undefined` for the `.toLowerCase()` below to throw on.
        const language = row.language?.trim() ?? "";
        const proficiency = row.proficiency?.trim() ?? "";

        if (!language && proficiency) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [index, "language"],
            message: "Language is required when a proficiency is provided.",
          });
        }
        const key = language.toLowerCase();
        if (language && !proficiency && mustHaveLevel.has(key)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [index, "proficiency"],
            message: REQUIRED_LANGUAGE_LEVEL_MESSAGE,
          });
        }
      });
    });

  return required ? base.min(1, "At least one language is required") : base;
};

const dedupeNames = (names) => {
  const seen = new Map();
  names.forEach((name) => {
    const key = name.toLowerCase();
    if (!seen.has(key)) seen.set(key, name);
  });
  return [...seen.values()];
};

// The rows a candidate is shown for one job: every language the job requires
// first, in the job's order and the job's spelling, then whatever else the
// candidate reported. A required row is always blank — the candidate answers
// the level themselves; an extracted level is never prefilled onto it. The
// job's demanded level is what they are measured against, never a default
// answer filled in for them. Extra (non-required) rows keep whatever the
// extraction reported.
//
// The job's rows are locked in the editor (see LanguageProficiencyRows), so
// this order is also what the candidate sees: answer the job first, add your
// own after.
//
// `keepReportedLevels` is for data the candidate already confirmed (Edit
// Details): there the level on a required row is their own answer, not an
// extracted guess, so it is kept rather than blanked.
export const mergeRequiredLanguageRows = (jobRequired, candidateRows, { keepReportedLevels = false } = {}) => {
  const required = toLanguageRows(jobRequired);
  const reported = toLanguageRows(candidateRows);
  if (!required.length) return reported;

  const claimed = new Set();
  const reportedLevel = new Map(reported.map((row) => [row.language.toLowerCase(), row.proficiency]));

  const requiredRows = dedupeNames(required.map((row) => row.language)).map((language) => {
    claimed.add(language.toLowerCase());
    return { language, proficiency: keepReportedLevels ? reportedLevel.get(language.toLowerCase()) || "" : "" };
  });

  return [...requiredRows, ...reported.filter((row) => !claimed.has(row.language.toLowerCase()))];
};

// The job-required names, deduplicated case-insensitively and kept in the job's
// spelling — what LanguageProficiencyRows locks. Which of those rows must carry
// a level is a separate question, answered by `levelledLanguageKeys`.
export const requiredLanguageNames = (jobRequired) =>
  dedupeNames(toLanguageRows(jobRequired).map((row) => row.language));
